/**
 * Which models page a tenant gets.
 *
 * A pick at the mount point, not a rewrite behind a condition. `TenantPage` is
 * not touched by this branch at all, so flag off renders exactly what shipped
 * before, including the columns and controls the new page drops. Rewriting the
 * old page in place would have made "flag off" mean "the old page minus the
 * parts I removed", which is worse than either state it is choosing between.
 *
 * Null while the flag resolves, so neither page renders and the list cannot
 * flash from one shape to the other on every load. Fail closed on any error,
 * so an outage in whatever serves the flag degrades to today's page rather
 * than to a blank one.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchTreeInsightsEnabled } from '../components/model/rgipChatApi';
import { legacyForced } from '../components/layout/legacy-mode';
import { LegacyExperienceToggle } from '../components/layout/legacy-experience-toggle';
import { TopbarSlot } from '../components/layout/topbar-slot';
import ModelsHome from './ModelsHome';
import TenantPage from './TenantPage';

export function ModelsRoute() {
  const { tenant } = useParams<{ tenant: string }>();
  const [revamped, setRevamped] = useState<boolean | null>(null);
  /**
   * Whether this browser declined the revamp.
   *
   * State resolved after mount, NOT read during render: the app is server-rendered and
   * hydrated, and `localStorage` does not exist on the server, so a render that reads it
   * cannot match the server's markup. Same reason `ModelPage` resolves it in
   * `componentDidMount`.
   */
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    let live = true;
    setOptedOut(legacyForced(tenant));
    fetchTreeInsightsEnabled(tenant)
      .catch(() => false)
      .then((enabled) => {
        if (live) setRevamped(enabled);
      });
    return () => {
      live = false;
    };
  }, [tenant]);

  if (revamped === null) return null;
  /**
   * Both directions, from the same page.
   *
   * #2628 gave the classic list a way back to the new one and returned before this
   * line, so the NEW list had no switch at all: somebody on the revamp who wanted
   * Classic had to open a customer fit model to find it, which is the exact
   * complaint #2628 was written to fix, on the other side of the same route.
   *
   * Unforced here, and for everyone on the revamp. `#2606` limited the model page's
   * switch to customer fit models because a model either is one or is not; a models
   * list is not one model, and this page is the same page for every tenant on the
   * revamp, so the restriction has nothing to apply to.
   */
  if (revamped) {
    return (
      <>
        <TopbarSlot>
          {/* `forced={false}`, and not `optedOut`: reaching this branch already
              proves they have not opted out. `fetchTreeInsightsEnabled` returns
              false when `legacyForced` is set, so an opt-out turns the flag's
              true into false at the source and this page never renders. */}
          <LegacyExperienceToggle tenantId={tenant} forced={false} />
        </TopbarSlot>
        <ModelsHome />
      </>
    );
  }

  /**
   * The way back, for somebody who opted out.
   *
   * The switch lives on the model page, and only on a customer fit model, so a person who
   * declined the revamp and then went to their models list had nothing on the page to
   * switch back with: they had to know to open a customer fit model to find it again.
   *
   * Only when they opted out. A tenant that never had the revamp has nothing to switch
   * away from and sees this page exactly as it is today, which is the whole point of
   * picking at the mount point rather than editing `TenantPage`.
   */
  return (
    <>
      {optedOut && (
        <TopbarSlot>
          <LegacyExperienceToggle tenantId={tenant} forced />
        </TopbarSlot>
      )}
      <TenantPage />
    </>
  );
}

export default ModelsRoute;
